/**
 * Leads API Service
 *
 * REST client for Lead CRUD, lifecycle status transition, opportunity conversion,
 * polymorphic link association, public ingestion, and Acumatica ERP synchronization.
 *
 * @see docs/ACUMATICA-INTEGRATION-2.md
 */

import api from '@/core/api'
import type {
    Lead,
    LeadLink,
    LeadExternalIdentifier,
    LeadFilters,
    PaginatedLeadsResponse,
    CreateLeadDto,
    UpdateLeadDto,
    LeadStatusTransitionDto,
    LeadConvertDto,
    LeadLinkDto,
    LeadExternalIdDto,
    LeadIngestDto,
} from './types'

export const leadService = {
    /**
     * List leads with pagination, filtering, searching, and sorting.
     * @see GET /leads
     */
    async list(filters?: LeadFilters): Promise<PaginatedLeadsResponse> {
        const { data } = await api.get<PaginatedLeadsResponse>('/leads', { params: filters })
        return data
    },

    /**
     * Get single lead by ID with touchpoints, links, and external identifiers.
     * @see GET /leads/:id
     */
    async get(id: string): Promise<Lead> {
        const { data } = await api.get<{ ok: boolean; data: Lead }>(`/leads/${id}`)
        return data.data
    },

    /**
     * Create or coalesce lead manually.
     * @see POST /leads
     */
    async create(payload: CreateLeadDto): Promise<Lead> {
        const { data } = await api.post<{ ok: boolean; data: Lead }>('/leads', payload)
        return data.data
    },

    /**
     * Update lead details.
     * @see PUT /leads/:id
     */
    async update(id: string, payload: UpdateLeadDto): Promise<Lead> {
        const { data } = await api.put<{ ok: boolean; data: Lead }>(`/leads/${id}`, payload)
        return data.data
    },

    /**
     * Soft delete lead record.
     * @see DELETE /leads/:id
     */
    async delete(id: string): Promise<void> {
        await api.delete(`/leads/${id}`)
    },

    /**
     * Transition lead lifecycle status.
     * @see POST /leads/:id/status
     */
    async transitionStatus(id: string, payload: LeadStatusTransitionDto): Promise<Lead> {
        const { data } = await api.post<{ ok: boolean; data: Lead }>(`/leads/${id}/status`, payload)
        return data.data
    },

    /**
     * Convert qualified lead to an opportunity.
     * @see POST /leads/:id/convert
     */
    async convert(id: string, payload: LeadConvertDto): Promise<Lead> {
        const { data } = await api.post<{ ok: boolean; data: Lead }>(`/leads/${id}/convert`, payload)
        return data.data
    },

    /**
     * Attach polymorphic link (property, contact, tenant, weather event, URL).
     * @see POST /leads/:id/links
     */
    async addLink(id: string, payload: LeadLinkDto): Promise<LeadLink> {
        const { data } = await api.post<{ ok: boolean; data: LeadLink }>(`/leads/${id}/links`, payload)
        return data.data
    },

    /**
     * Remove reference link.
     * @see DELETE /leads/:id/links/:linkId
     */
    async removeLink(id: string, linkId: string): Promise<void> {
        await api.delete(`/leads/${id}/links/${linkId}`)
    },

    /**
     * Attach or update external identifier (e.g. Acumatica NoteID).
     * @see POST /leads/:id/external-ids
     */
    async addExternalId(id: string, payload: LeadExternalIdDto): Promise<LeadExternalIdentifier> {
        const { data } = await api.post<{ ok: boolean; data: LeadExternalIdentifier }>(`/leads/${id}/external-ids`, payload)
        return data.data
    },

    /**
     * Public web form ingestion (supports honeypot anti-spam and X-Embed-Key).
     * @see POST /leads/ingest
     */
    async ingest(payload: LeadIngestDto, embedKey?: string): Promise<{ ok: boolean; message?: string }> {
        const headers: Record<string, string> = {}
        if (embedKey) {
            headers['X-Embed-Key'] = embedKey
        }
        const { data } = await api.post<{ ok: boolean; message?: string }>('/leads/ingest', payload, { headers })
        return data
    },

    /**
     * Trigger manual Acumatica ERP sync for one or multiple leads.
     * @see POST /integrations/acumatica/sync
     */
    async syncToAcumatica(
        id: string | string[],
        forceSync = false,
    ): Promise<{ ok: boolean; data: { message: string; jobId?: string; count: number } }> {
        const entityIds = Array.isArray(id) ? id : [id]
        const { data } = await api.post<{ ok: boolean; data: { message: string; jobId?: string; count: number } }>(
            '/integrations/acumatica/sync',
            {
                entityType: 'lead',
                entityIds,
                forceSync,
            },
        )
        return data
    },
}
